// src/hooks/useFetchAllForms.js

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext'; // For currentUser, organizationId
import { getFormsForUser } from '../utils/firestore';

/**
 * A custom hook providing an infinite-scrolling approach to load forms 
 * ("fetch all forms" in chunks) from Firestore. 
 * 
 * - We keep a single array (`forms`) that accumulates data from each chunk.
 * - Each call to `fetchNextBatch` loads the next chunk from Firestore 
 *   using `lastDoc` for pagination.
 * - We track `hasMore` to know if further loading is possible, 
 *   and `isLoading` to show a spinner if desired.
 *
 * Usage: 
 *   const { forms, isLoading, hasMore, fetchNextBatch } = useFetchAllForms(20);
 * 
 *   // ... display forms in a grid, call fetchNextBatch on "Load more" or infinite scroll
 */
export function useFetchAllForms(pageSize = 20) {
  const { currentUser, organizationId, initialized, authLoading } = useAuth();

  // Our master array of loaded forms
  const [forms, setForms] = useState([]);
  // We store the last document reference for Firestore pagination
  const [lastDoc, setLastDoc] = useState(null);
  // Whether we are currently fetching a new batch
  const [isLoading, setIsLoading] = useState(false);
  // Whether more forms remain to be loaded
  const [hasMore, setHasMore] = useState(true);

  // We'll track if user changes
  const prevUserIdRef = useRef(null);

  /**
   * If the user changes (or we haven't loaded anything yet), 
   * reset and automatically load the first batch.
   */
  useEffect(() => {
    if (!initialized || authLoading) return;
    if (!currentUser?.uid || !organizationId) return;

    // If this is a new user or no forms loaded, reset everything
    if (currentUser.uid !== prevUserIdRef.current) {
      setForms([]);
      setLastDoc(null);
      setHasMore(true);
      prevUserIdRef.current = currentUser.uid;
      fetchNextBatch(); // load the first chunk
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, authLoading, currentUser?.uid, organizationId]);

  /**
   * fetchNextBatch loads the next chunk of forms, appending to `forms`.
   */
  const fetchNextBatch = useCallback(async () => {
    if (isLoading) return;       // avoid double calls
    if (!hasMore) return;        // no more forms to load
    if (!currentUser?.uid || !organizationId) return;

    setIsLoading(true);

    try {
      // call getFormsForUser with lastDoc pagination
      const {
        status,
        forms: fetchedForms,
        lastVisibleDoc,
      } = await getFormsForUser(
        currentUser.uid,
        organizationId,
        lastDoc, 
        pageSize
      );

      if (status === 'success') {
        if (fetchedForms.length === 0) {
          // no new forms => no more to load
          setHasMore(false);
        } else {
          // append them to existing
          setForms((prev) => [...prev, ...fetchedForms]);
          setLastDoc(lastVisibleDoc || null);

          // if fewer forms than pageSize, no further data
          if (fetchedForms.length < pageSize) {
            setHasMore(false);
          }
        }
      } else {
        console.warn('[useFetchAllForms] fetchNextBatch: no forms or an error from getFormsForUser');
        setHasMore(false);
      }
    } catch (error) {
      console.error('[useFetchAllForms] ❌ Error fetching next batch:', error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [
    isLoading, 
    hasMore, 
    currentUser, 
    organizationId, 
    lastDoc, 
    pageSize
  ]);

  return {
    forms,         // The array of all forms loaded so far
    isLoading,     // Whether we're fetching a new batch
    hasMore,       // Whether there's more data to load
    fetchNextBatch // Function to load more
  };
}
